<!-- ═══ Read-only inline row: a submitted SDR ═══ -->
@if (isReadOnly) {
  <div class="isdr-readonly">
    <!-- Offline-created, not yet replayed by uploadPendingSdrs — statusCode already
         reads Submitted at this point, so this badge is the only cue the record
         hasn't actually reached Dynamics yet. -->
    <div class="isdr-pending-badge" *ngIf="sdr?.pendingSync">
      <ion-icon name="time-outline"></ion-icon>{{ 'PENDING_SYNC' | translate }}
    </div>
    <div class="isdr-ro-row">
      <span class="isdr-ro-label">{{ 'CUSTOMER' | translate }}</span>
      <span class="isdr-ro-value">{{ customerName || ('NO_DATA' | translate) }}</span>
    </div>
    <div class="isdr-ro-row" *ngIf="readOnlyDeliveryAddress">
      <span class="isdr-ro-label">{{ 'DELIVERY_ADDRESS' | translate }}</span>
      <span class="isdr-ro-value">{{ readOnlyDeliveryAddress }}</span>
    </div>
    <div class="isdr-ro-row" *ngIf="readOnlyEmail">
      <span class="isdr-ro-label">{{ 'EMAIL_ADDRESS' | translate }}</span>
      <span class="isdr-ro-value">{{ readOnlyEmail }}</span>
    </div>
    <div class="isdr-ro-products" *ngIf="sdr?.products?.length">
      <span class="isdr-ro-label">{{ 'PRODUCTS' | translate }}</span>
      <div class="isdr-ro-product-grid">
        <div class="isdr-ro-grid-header">
          <span class="isdr-ro-gc-product">{{ 'PRODUCT' | translate }}</span>
          <span class="isdr-ro-gc-qty">{{ 'QUANTITY' | translate }}</span>
        </div>
        <div class="isdr-ro-grid-row" *ngFor="let p of sdr.products; trackBy: trackByIndex">
          <span class="isdr-ro-gc-product isdr-ro-product-name">{{ p.skuName }}</span>
          <span class="isdr-ro-gc-qty">
            <span class="isdr-ro-qty-badge">{{ p.qty }}</span>
          </span>
        </div>
      </div>
    </div>
    <!-- Read-only notes / attachments -->
    <sdr-notes-section
      [sdrId]="null"
      [existingNotes]="sdr?.notes ?? []"
      [readOnly]="true"
      [showSectionHeader]="false">
    </sdr-notes-section>
  </div>
}

<!-- ═══ Editable draft ═══ -->
@if (!isReadOnly) {
  <div class="isdr-form" [class.isdr-form--submitting]="isSubmitting">

    <!-- In-form loader, matching Sample Drop / Promotional Material's own
         saving overlay. Covers BOTH submit and save-on-click autosave, since
         a rep adding a product or note otherwise got no feedback at all
         (OMNI-61724). -->
    <div class="isdr-busy-overlay" *ngIf="isSubmitting || isBusy">
      <ion-spinner name="crescent"></ion-spinner>
    </div>


    <!-- Customer (ind-form-field LIST — mirrors the Drops tab / IAO and the
         full-page SDR form's Customer field so this looks like the rest of the app).
         Wrapped in .sdr-section like every other field below, matching IAO's
         `.iao-section.iao-contact-section` wrapper — without it, this field loses
         the same top spacing/rhythm the rest of the form gets. -->
    <div class="sdr-section sdr-contact-section">
      <ind-form-field
        [viewData]="contactFormField"
        [nearMePillsLoader]="nearMePillsLoader"
        [sharedLocationPillsLoader]="sharedLocationPillsLoader"
        (handleAdditionalControls)="onContactFormAdditionalControl($event)"
        (onInputClick)="onCustomerFieldClicked()">
      </ind-form-field>
    </div>

    <!-- Delivery Address -->
    <div class="sdr-section" [class.sdr-section--invalid]="isDeliveryAddressInvalid && !isAlternateAddressActive && !isAddressDropdownOpen">
      <div class="sdr-section-body" [class.sdr-section-body--no-divider]="isAlternateAddressActive">
        <div class="sdr-section-label sdr-section-label--tight">
          {{ 'DELIVERY_ADDRESS' | translate }}
          <ion-icon class="mandatory-icon"></ion-icon>
        </div>

        <ng-container *ngIf="!isAlternateAddressActive">
          <div class="sdr-value-row"
               [class.sdr-value-row--clickable]="addressOptions.length >= 1"
               (click)="addressOptions.length >= 1 && toggleAddressDropdown()">
            <span class="sdr-value" *ngIf="formState?.deliveryAddressId">{{ formState.deliveryAddressLabel }}</span>
            <span class="sdr-value sdr-value--empty" *ngIf="!formState?.deliveryAddressId">
              {{ (addressOptions.length ? 'SDR_SELECT_DELIVERY_ADDRESS' : 'NO_DELIVERY_ADDRESS') | translate }}
            </span>
            <div class="sdr-value-actions">
              <span class="sdr-badge sdr-badge--primary" *ngIf="isDeliveryAddressPrimary">
                {{ 'ACCOUNT_PRIMARY_CONTACT_NAME' | translate }}
              </span>
              <ion-icon *ngIf="addressOptions.length >= 1"
                        [name]="isAddressDropdownOpen ? 'chevron-up' : 'chevron-down'"
                        class="sdr-chevron-icon"></ion-icon>
            </div>
          </div>
        </ng-container>

        <ng-container *ngIf="isAlternateAddressActive">
          <textarea class="sdr-alt-input" rows="2"
                    maxlength="500"
                    [class.sdr-alt-input--error]="isDeliveryAddressInvalid"
                    [placeholder]="'SDR_ALT_ADDRESS_PLACEHOLDER' | translate"
                    [value]="formState.alternateDeliveryAddress"
                    (input)="onAlternateAddressInput($event)"
                    (blur)="onAlternateAddressBlur()"></textarea>
          <div class="sdr-alt-error" *ngIf="isDeliveryAddressInvalid">{{ 'THIS_IS_A_REQUIRED_FIELD' | translate }}</div>
          <button type="button" class="sdr-alt-back" *ngIf="hasSavedAddresses" (click)="useSavedAddress()">
            <ion-icon name="chevron-back-outline"></ion-icon>{{ 'SDR_USE_SAVED_ADDRESS' | translate }}
          </button>
        </ng-container>
      </div>
      <div class="sdr-backdrop" *ngIf="isAddressDropdownOpen && !isAlternateAddressActive" (click)="toggleAddressDropdown()"></div>
      <div class="sdr-dropdown" *ngIf="isAddressDropdownOpen && !isAlternateAddressActive">
        <div class="sdr-dropdown-item"
             *ngFor="let addr of addressOptions; trackBy: trackByOptionId"
             [class.sdr-dropdown-item--selected]="addr.id === formState?.deliveryAddressId"
             (click)="selectAddress(addr)">
          <div class="sdr-dropdown-text">{{ addr.label }}</div>
          <span class="sdr-badge sdr-badge--primary" *ngIf="addr.isPrimary">
            {{ 'ACCOUNT_PRIMARY_CONTACT_NAME' | translate }}
          </span>
        </div>
        <div class="sdr-dropdown-alt" (click)="enableAlternateAddress()">
          <ion-icon name="create-outline"></ion-icon>{{ 'SDR_ENTER_DIFFERENT_ADDRESS' | translate }}
        </div>
      </div>
    </div>

    <!-- Email Address -->
    <div class="sdr-section" [class.sdr-section--invalid]="isEmailInvalid && !isAlternateEmailActive && !isEmailDropdownOpen">
      <div class="sdr-section-body" [class.sdr-section-body--no-divider]="isAlternateEmailActive">
        <div class="sdr-section-label sdr-section-label--tight">
          {{ 'EMAIL_ADDRESS' | translate }}
          <ion-icon class="mandatory-icon" *ngIf="isEmailMandatory"></ion-icon>
        </div>

        <ng-container *ngIf="!isAlternateEmailActive">
          <div class="sdr-value-row"
               [class.sdr-value-row--clickable]="emailOptions.length >= 1"
               (click)="emailOptions.length >= 1 && toggleEmailDropdown()">
            <span class="sdr-value" *ngIf="formState?.emailId">{{ formState.emailLabel }}</span>
            <span class="sdr-value sdr-value--empty" *ngIf="!formState?.emailId">
              {{ (emailOptions.length ? 'SDR_SELECT_EMAIL' : 'NO_EMAIL') | translate }}
            </span>
            <div class="sdr-value-actions">
              <span class="sdr-badge sdr-badge--primary" *ngIf="isEmailPrimary">
                {{ 'ACCOUNT_PRIMARY_CONTACT_NAME' | translate }}
              </span>
              <ion-icon *ngIf="emailOptions.length >= 1"
                        [name]="isEmailDropdownOpen ? 'chevron-up' : 'chevron-down'"
                        class="sdr-chevron-icon"></ion-icon>
            </div>
          </div>
        </ng-container>

        <ng-container *ngIf="isAlternateEmailActive">
          <input class="sdr-alt-input" type="email"
                 [class.sdr-alt-input--error]="isEmailInvalid || isAlternateEmailInvalid"
                 [placeholder]="'SDR_ALT_EMAIL_PLACEHOLDER' | translate"
                 [value]="formState.alternateEmail"
                 (input)="onAlternateEmailInput($event)"
                 (blur)="onAlternateEmailBlur()" />
          <div class="sdr-alt-error" *ngIf="isAlternateEmailInvalid">{{ 'SDR_INVALID_EMAIL' | translate }}</div>
          <div class="sdr-alt-error" *ngIf="isEmailInvalid && !isAlternateEmailInvalid">{{ 'THIS_IS_A_REQUIRED_FIELD' | translate }}</div>
          <button type="button" class="sdr-alt-back" *ngIf="hasSavedEmails" (click)="useSavedEmail()">
            <ion-icon name="chevron-back-outline"></ion-icon>{{ 'SDR_USE_SAVED_EMAIL' | translate }}
          </button>
        </ng-container>
      </div>
      <div class="sdr-backdrop" *ngIf="isEmailDropdownOpen && !isAlternateEmailActive" (click)="toggleEmailDropdown()"></div>
      <div class="sdr-dropdown" *ngIf="isEmailDropdownOpen && !isAlternateEmailActive">
        <div class="sdr-dropdown-item"
             *ngFor="let opt of emailOptions; trackBy: trackByOptionId"
             [class.sdr-dropdown-item--selected]="opt.id === formState?.emailId"
             (click)="selectEmail(opt)">
          <div class="sdr-dropdown-text">{{ opt.label }}</div>
          <span class="sdr-badge sdr-badge--primary" *ngIf="opt.isPrimary">
            {{ 'ACCOUNT_PRIMARY_CONTACT_NAME' | translate }}
          </span>
        </div>
        <div class="sdr-dropdown-alt" (click)="enableAlternateEmail()">
          <ion-icon name="create-outline"></ion-icon>{{ 'SDR_ENTER_DIFFERENT_EMAIL' | translate }}
        </div>
      </div>
    </div>

    <!-- Products -->
    <div class="sdr-section">
      <div class="sdr-section-label">
        {{ 'PRODUCTS' | translate }}
        <ion-icon class="mandatory-icon"></ion-icon>
        <span class="sdr-section-count" *ngIf="formState?.products?.length">
          ({{ formState.products.length }})
        </span>
      </div>

      <div class="sdr-product-search-wrap"
           [class.sdr-product-search-wrap--invalid]="isProductsInvalid">
        <ion-icon name="search-outline" class="sdr-search-icon"></ion-icon>
        <input type="text"
               class="sdr-product-search-input"
               [placeholder]="'ADD_PRODUCT' | translate"
               [(ngModel)]="pickerSearchText"
               (focus)="openPicker()"
               (input)="onPickerInput()">
      </div>

      <div class="sdr-backdrop" *ngIf="isPickerOpen" (click)="closePicker()"></div>

      <div class="sdr-picker-anchor" *ngIf="isPickerOpen">
        <div class="sdr-picker-dropdown">
          <div *ngIf="filteredPickerSkus.length === 0" class="sdr-picker-empty">
            {{ 'NO_PRODUCTS_FOUND' | translate }}
          </div>
          <div *ngFor="let sku of filteredPickerSkus; trackBy: trackById"
               class="sdr-picker-row"
               [class.sdr-picker-row--alreadyAdded]="isSkuInForm(sku.skuId)"
               (click)="onPickerRowClick(sku)">
            <div class="sdr-picker-row-info">
              <span class="sdr-picker-row-name">{{ sku.skuName }}</span>
              <span class="sdr-micro-pill sdr-micro-pill--blue" *ngIf="hasSkuLimit(sku.skuId)">
                {{ getSkuLimitLabel(sku.skuId) }}
              </span>
            </div>
            <ion-icon name="checkmark-circle"
                      class="sdr-picker-row-check"
                      *ngIf="isSkuInForm(sku.skuId)"></ion-icon>
          </div>
        </div>
      </div>

      <div class="sdr-section-error sdr-section-error--inline"
           *ngIf="isProductsInvalid && !isPickerOpen">
        {{ 'SDR_PRODUCTS_REQUIRED' | translate }}
      </div>

      <div class="sdr-grid" *ngIf="formState?.products?.length > 0">
        <div class="sdr-grid-header">
          <div class="sdr-gc sdr-gc-product">{{ 'PRODUCT' | translate }}</div>
          <div class="sdr-gc sdr-gc-qty">{{ 'QUANTITY' | translate }}</div>
          <div class="sdr-gc sdr-gc-action"></div>
        </div>
        <ng-container *ngFor="let product of formState.products; let i = index; trackBy: trackByIndex">
          <div class="sdr-grid-row"
               [class.sdr-grid-row--error]="getQtyErrorKey(product)">
            <div class="sdr-gc sdr-gc-product">
              <span class="sdr-grid-product-name">{{ product.skuName }}</span>
              <span class="sdr-micro-pill sdr-micro-pill--blue" *ngIf="hasSkuLimit(product.skuId)">
                {{ getSkuLimitLabel(product.skuId) }}
              </span>
              <!-- Customer's remaining Manual-Distribution allocation balance
                   (OMNI-59706), when a Customer Allocation record was found. -->
              <span class="sdr-micro-pill sdr-micro-pill--green" *ngIf="hasAllocationInfo(product)">
                {{ getAllocationLabel(product) }}
              </span>
              <!-- OMNI-62147. Remaining is already on the pill above, and
                   Allocated is just Eligible minus it, so neither is repeated. -->
              <div class="sdr-alloc-details" *ngIf="hasAllocationDetails(product)">
                <span *ngIf="!product.allocationUnlimited">{{ 'ELIGIBLE_QUANTITY' | translate }}: {{ product.allocationEligible }}</span>
                <span *ngIf="getAllocationValidity(product) as validity">
                  {{ 'VALIDITY_PERIOD' | translate }}: {{ validity }}
                </span>
              </div>
            </div>
            <div class="sdr-gc sdr-gc-qty">
              <div class="sdr-stepper">
                <button type="button" class="sdr-stepper-btn"
                        (click)="updateQty(i, -1)"
                        [disabled]="isDecrementDisabled(product)">−</button>
                <input class="sdr-qty-input" type="number" min="0"
                       [value]="product.qty"
                       (change)="onGridQtyInput(i, $event)"
                       (blur)="onGridQtyInput(i, $event)">
                <button type="button" class="sdr-qty-dropdown-btn"
                        (click)="openQtyDropdown($event, i)"
                        [attr.aria-label]="'QUANTITY' | translate">
                  <ion-icon name="chevron-down-outline"></ion-icon>
                </button>
                <button type="button" class="sdr-stepper-btn"
                        (click)="updateQty(i, 1)"
                        [disabled]="isIncrementDisabled(product)">+</button>
              </div>
            </div>
            <div class="sdr-gc sdr-gc-action">
              <button type="button" class="sdr-grid-remove-btn" (click)="removeProduct(i)">
                <ion-icon name="close-outline"></ion-icon>
              </button>
            </div>
          </div>

          <div class="sdr-grid-error-row" *ngIf="getQtyErrorKey(product) as qtyErrorKey">
            <ion-icon name="warning-outline"></ion-icon>
            <span>{{ qtyErrorKey | translate }}</span>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Notes & Attachments (config-driven; staged notes ride the submit batch). -->
    <sdr-notes-section
      [sdrId]="null"
      [existingNotes]="[]"
      [readOnly]="false"
      [showSectionHeader]="false"
      [initialStagedNotes]="_seededNotes"
      (stagedNoteAdded)="onStagedNoteAdded($event)"
      (stagedNoteEdited)="onStagedNoteEdited($event)"
      (stagedNoteRemoved)="onStagedNoteRemoved($event)">
    </sdr-notes-section>

    <!-- Submit — atomic (no autosave): one batch call, same as the standalone SDR form.
         Styled to match Sample Drop's own submit box (inline-allocation-order.html's
         .iao-sign-here-btn, no-signature-required variant) — plain button, not
         ion-button, so the dashed/solid + opacity states can be replicated exactly
         without fighting ion-button's shadow-dom internals. -->
    <div class="isdr-footer">
      <button class="isdr-submit-btn"
              [class.isdr-submit-btn--active]="isSubmitEnabled"
              [disabled]="!isSubmitEnabled"
              (click)="submit()">
        <span class="isdr-submit-btn-label">{{ (isSubmitting ? 'SUBMITTING' : 'SUBMIT') | translate }}</span>
      </button>
    </div>

  </div>
}
